import classNames from 'classnames';

type SegmentOption<T extends string | number> = {
  value: T;
  label: string;
};

type MiniSegmentedControlProps<T extends string | number> = {
  /** Accessible name for the group; the visible label lives with the caller. */
  ariaLabel: string;
  options: SegmentOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Locks the control behind the paywall and routes clicks to the modal. */
  isLocked?: boolean;
  onLockedClick?: () => void;
};

/**
 * Compact single-choice control: one segment per option inside a shared
 * track, with the active segment filled in the brand blue.
 */
export const MiniSegmentedControl = <T extends string | number>({
  ariaLabel,
  options,
  value,
  onChange,
  isLocked = false,
  onLockedClick
}: MiniSegmentedControlProps<T>) => (
  <div
    role="group"
    aria-label={ariaLabel}
    className="inline-flex items-center gap-0.5 rounded-lg bg-neutral-100 p-0.5"
  >
    {options.map((option) => {
      const isActive = option.value === value;
      return (
        <button
          key={String(option.value)}
          type="button"
          aria-pressed={isActive}
          onClick={() =>
            isLocked ? onLockedClick?.() : onChange(option.value)
          }
          className={classNames(
            'min-h-[32px] whitespace-nowrap rounded-md px-2.5 py-1 text-[13px] transition-colors',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600',
            isActive
              ? 'bg-primary-600 font-bold text-white shadow-sm'
              : 'font-semibold text-neutral-600 hover:text-neutral-900'
          )}
        >
          {option.label}
        </button>
      );
    })}
  </div>
);
